import { useEffect, useState } from 'react'
import { Card } from '@/components/Card'
import { StatusBadge } from '@/components/StatusBadge'
import { fetchApi } from '@/lib/api'
import { RefreshCw } from 'lucide-react'
import { formatDateTime } from '@/lib/date'

type ECSService = {
  name: string
  status: string
  desired: number
  running: number
  pending: number
  taskDefinition: string
  lastDeployment: string | null
}

export function ECS() {
  const [env, setEnv] = useState<'prod' | 'dev'>('prod')
  const [services, setServices] = useState<ECSService[]>([])
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const data = await fetchApi<{ services: ECSService[] }>(`/ecs/services/${env}`)
      setServices(data.services)
    } catch {
      setServices([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [env])

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">ECS Services</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded overflow-hidden border border-[var(--color-border)]">
            {(['prod', 'dev'] as const).map((e) => (
              <button
                key={e}
                onClick={() => setEnv(e)}
                className={`px-3 py-1 text-sm ${
                  env === e
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'bg-[var(--color-bg-card)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                {e.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <Card>
        {loading ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-muted)]">
                  <th className="pb-2 font-medium">Service</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Tasks</th>
                  <th className="pb-2 font-medium">Task Definition</th>
                  <th className="pb-2 font-medium">Last Deploy</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <tr key={svc.name} className="border-b border-[var(--color-border)]/50">
                    <td className="py-2 font-mono">{svc.name}</td>
                    <td className="py-2">
                      <StatusBadge
                        status={svc.running === svc.desired ? 'ok' : 'warning'}
                        label={svc.status}
                      />
                    </td>
                    <td className="py-2">
                      <span className="text-green-400">{svc.running}</span>
                      <span className="text-[var(--color-text-muted)]">/{svc.desired}</span>
                      {svc.pending > 0 && (
                        <span className="text-yellow-400 ml-1">({svc.pending} pending)</span>
                      )}
                    </td>
                    <td className="py-2 font-mono text-xs text-[var(--color-text-muted)]">
                      {svc.taskDefinition}
                    </td>
                    <td className="py-2 text-xs text-[var(--color-text-muted)]">
                      {svc.lastDeployment
                        ? formatDateTime(svc.lastDeployment)
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
